import { useEffect, useRef, useState } from 'react'
import { useMoteConfigContext } from '@mote/recipes'
import { useTrackMutation } from '../../../api'
import { TRACK_EVENT_NAMES, noop } from '../../constants'
import { useFlightRecorder } from '../../contexts'
import { Diagnostics, DiagnosticsLogger } from '../../diagnostics'
import { canTrustFreeMotesAllocation } from '../../identity'
import { useAudioStream } from '../audio'
import { useMicrophonePermission } from '../microphone'
import { useCountdown } from '../useCountdown'
import { useUnmountEffect } from '../useUnmountEffect'
import { moteRecorderErrorMap, useMoteRecorderErrorTrack } from './errors'
import { RECORDER_STATUS, useRecorder } from './useRecorder'

const logD = new DiagnosticsLogger('useMoteRecorder', 'Mote recorder')

/**
 * @typedef {Object} MoteRecorderProps
 * @property {boolean} manualStatusHandling
 * @property {() => Record<string, any>} getRegisterExtraPayload
 * @property {() => void} onStopRecording
 * @property {() => void} onStartRecording
 * @property {() => void} onPauseRecording
 * @property {() => void} onResumeRecording
 * @property {() => void} onCancelRecording
 * @property {() => void} onRecordingTimeout
 * @property {() => void} onFinalizeRecording
 * @property {() => void} onReset
 * @property {() => void} onMount
 * @property {() => void} onUnmount
 * @property {() => void} onError
 * @property {() => boolean} getIsLoggedIn
 * @property {() => boolean} hasOptedOutOfTranscription
 * @property {() => string} getTranscriptionLanguage
 * @property {() => Record<string, string>} getExtraTrackingPayload
 * @property {boolean} shouldRedirect
 */

/**
 * @typedef {Object} MoteRecorderReturn
 * @property {() => void} startRecording
 * @property {() => void} pauseRecording
 * @property {() => void} resumeRecording
 * @property {(reason?: any) => void} cancelRecording
 * @property {() => void} stopRecording
 * @property {() => void} reset
 * @property {Record<string, string | boolean>} status
 * @property {number} maxSeconds
 * @property {number} seconds
 * @property {boolean} chunksInFlight
 * @property {MoteRecorderError | null} error
 * @property {string | null} permissionStatus
 * @property {boolean} permissionResponseReceived
 * @property {boolean} loadingPollingLimit
 * @property {MediaStreamAudioSourceNode} audioStream
 * @property {AudioContext} audioContext
 * @property {any} registeredMote
 * @property ERRORS
 */

/**
 * @param {MoteRecorderProps} props
 * @returns {MoteRecorderReturn}
 */
export const useMoteRecorder = props => {
  const moteConfigContext = useMoteConfigContext()
  const {
    onStopRecording = noop,
    onStartRecording = noop,
    onPauseRecording = noop,
    onResumeRecording = noop,
    onCancelRecording = noop,
    onRecordingTimeout = noop,
    onFinalizeRecording = noop,
    onReset = noop,
    onMount = noop,
    onUnmount = noop,
    onError = noop,
    getIsLoggedIn,
    hasOptedOutOfTranscription,
    getTranscriptionLanguage,
    getExtraTrackingPayload = () => ({}),
    getRegisterExtraPayload = () => ({}),
    shouldRedirect,
    manualStatusHandling
  } = props
  const [loadingPollingLimit, setPollingPastLimit] = useState(false)
  const [maxSeconds, setMaxSeconds] = useState(moteConfigContext.maxSeconds)
  const [error, setError] = useState(null)
  const {
    start,
    stop,
    pause,
    resume,
    cancel,
    status,
    finalizeData,
    finalizeRecording,
    uploaderError,
    resetStatus,
    cleanup,
    registeredMote,
    streamingTranscriptData,
    handleStatusChange,
    error: recorderError
  } = useRecorder({
    manualStatusHandling,
    maxSeconds,
    shouldRedirect,
    getRegisterExtraPayload,
    hasOptedOutOfTranscription
  })
  const cancelPollingTimeoutRef = useRef(null)
  const recordingInitiated = useRef(false)
  const [stoppingDeferred, setStoppingDeferred] = useState(false)
  const { flightState, chunksInFlight } = useFlightRecorder()
  const { mutate: track } = useTrackMutation()
  const trackRecorderError = useMoteRecorderErrorTrack()

  const [seconds, { start: startTimer, stop: stopTimer, reset: resetTimer }] =
    useCountdown({ seconds: maxSeconds, lowerBoundaryLimit: 0 })
  const {
    start: startAudioStream,
    stop: stopAudioStream,
    pause: pauseAudioStream,
    resume: resumeAudioStream,
    audioContext,
    audioStream
  } = useAudioStream()
  const { permissionStatus, permissionResponseReceived } =
    useMicrophonePermission()
  const shouldErrorOut = error || recorderError || uploaderError

  function handleInternalStatusChange(status) {
    handleStatusChange(status, !manualStatusHandling)
  }

  function handle403Error() {
    logD.warn('Not logged in')
    track({ variables: { event: TRACK_EVENT_NAMES.error.moteCreateLogin } })
    setError(moteRecorderErrorMap.NOT_LOGGED_IN)
  }

  const startRecording = async () => {
    if (recordingInitiated.current) {
      logD.warn('Recording already initiated')
      return
    }

    if (!getIsLoggedIn()) {
      handle403Error()
      return
    }

    if (
      Object.keys(flightState).length > 0 &&
      (flightState.usedFreeMotes && flightState.usedFreeMotes) !== null &&
      flightState.usedFreeMotes >= flightState.totalFreeMotes &&
      flightState.shouldDebit &&
      canTrustFreeMotesAllocation()
    ) {
      track({ variables: { event: TRACK_EVENT_NAMES.quota.exhausted } })
      setError(moteRecorderErrorMap.QUOTA_EXCEEDED)
      return
    }

    logD.info('Recording initiated')
    resetTimer()
    recordingInitiated.current = true

    const stream = await start()

    if (!(stream instanceof MediaStream)) {
      cancelRecording()
      return
    }

    handleInternalStatusChange(RECORDER_STATUS.recording)
    startAudioStream(stream)
    setMaxSeconds(moteConfigContext.maxSeconds)

    logD.info(
      `Max seconds allowed for recording: ${moteConfigContext.maxSeconds}`
    )

    startTimer()
    onStartRecording()

    track({
      variables: {
        ...getExtraTrackingPayload().onRecordStart,
        event: TRACK_EVENT_NAMES.mote.record.start
      }
    })
  }

  const pauseRecording = () => {
    stopTimer()
    pause()
    pauseAudioStream()
    handleInternalStatusChange(RECORDER_STATUS.paused)
    onPauseRecording()
  }

  const resumeRecording = () => {
    startTimer()
    resume()
    resumeAudioStream()
    handleInternalStatusChange(RECORDER_STATUS.recording)
    onResumeRecording()
  }

  const stopRecording = async () => {
    if (!(status.isRecording || status.isPaused)) {
      return
    }
    stopAudioStream(true)

    const conditionsMet = await stop(getFinalizedAdditionalPayload())
    handleInternalStatusChange(RECORDER_STATUS.stopped)
    if (!conditionsMet) {
      logD.info('Deferring')
      setStoppingDeferred(true)
      return
    }

    recordingInitiated.current = false
    onStopRecording()
  }

  const cancelRecording = reason => {
    recordingInitiated.current = false
    resetTimer()
    cancel({ flushOldData: !shouldErrorOut })
    stopAudioStream(true)
    handleInternalStatusChange(RECORDER_STATUS.cancelled)
    onCancelRecording(reason)
  }

  const clearPollingTimeout = () => {
    if (!cancelPollingTimeoutRef.current) return

    clearTimeout(cancelPollingTimeoutRef.current)
    cancelPollingTimeoutRef.current = null
  }

  useEffect(() => {
    if (seconds > 0) {
      return
    }
    stopRecording()
    onRecordingTimeout()
  }, [seconds])

  useEffect(() => {
    if (!stoppingDeferred || uploaderError) return
    if (!registeredMote) return
    if (chunksInFlight) return

    logD.info('Triggering finalize')
    finalizeRecording(getFinalizedAdditionalPayload())

    setStoppingDeferred(false)
  }, [chunksInFlight, stoppingDeferred, uploaderError, registeredMote])

  useEffect(() => {
    if (uploaderError || !registeredMote || chunksInFlight) return
    if (!finalizeData) {
      if (cancelPollingTimeoutRef.current || !status.isStopped) return
      logD.info('Triggering finalize polling')
      cancelPollingTimeoutRef.current = setTimeout(() => {
        setPollingPastLimit(true)
        cancelPollingTimeoutRef.current = setTimeout(() => {
          setError(moteRecorderErrorMap.POLLING_THRESHOLD_EXCEEDED)
          recordingInitiated.current = false

          logD.error('Polling for finalized data time limit exceeded')

          Diagnostics.dispatch({
            userVisibleErrorMessage:
              'Something went wrong with your recording. Please try again or kindly contact support',
            dispatchContext: 'MOTE_RECORDER',
            title: 'Mote finalize polling time limit exceeded'
          })

          trackRecorderError(moteRecorderErrorMap.POLLING_THRESHOLD_EXCEEDED)
        }, 15000)
      }, 20000)
      return
    }

    if (!status.isStopped) return

    track({
      variables: {
        ...getExtraTrackingPayload().onFinalize,
        event: TRACK_EVENT_NAMES.mote.action.create,
        contentAssetId: finalizeData.moteStatus.moteId,
        assetLength: maxSeconds - seconds,
        transcriptionOptOut: hasOptedOutOfTranscription(),
        transcriptionLanguage: getTranscriptionLanguage()
      }
    })

    clearTimeout(cancelPollingTimeoutRef.current)
    recordingInitiated.current = false
    cancelPollingTimeoutRef.current = null
    resetStatus()
    handleInternalStatusChange(RECORDER_STATUS.inactive)
    finalizeData.moteStatus.duration = maxSeconds - seconds
    onFinalizeRecording(finalizeData)
  }, [
    finalizeData,
    status.isStopped,
    uploaderError,
    registeredMote,
    chunksInFlight
  ])

  useEffect(onMount, [])
  useUnmountEffect(onUnmount, [])

  function reset() {
    setError(null)
    cleanup({ flushOldData: true, status: 'inactive' })
    handleInternalStatusChange(RECORDER_STATUS.inactive)
    onReset()
  }

  useEffect(() => {
    if (!uploaderError && !recorderError) return

    if (cancelPollingTimeoutRef.current) {
      clearTimeout(cancelPollingTimeoutRef.current)
      cancelPollingTimeoutRef.current = null
    }

    const localError = uploaderError || recorderError

    // handle network errors
    if (error instanceof TypeError) {
      cancelRecording()
      clearPollingTimeout()
      setError(moteRecorderErrorMap.NETWORK_ERROR)

      logD.error('Network issue')
      trackRecorderError(moteRecorderErrorMap.NETWORK_ERROR)
      return
    }

    const errorCode =
      typeof localError === 'string'
        ? localError
        : localError.response?.status || localError.name

    // handle API errors
    switch (errorCode) {
      case moteRecorderErrorMap.CHUNKS_ERROR_THRESHOLD_ERROR:
        setError(moteRecorderErrorMap.CHUNKS_ERROR_THRESHOLD_ERROR)
        Diagnostics.dispatch({
          userVisibleErrorMessage:
            'There was an error recording your mote. Please try again, or search our FAQ pages for troubleshooting suggestions.',
          dispatchContext: 'MOTE_RECORDER',
          title:
            'Chunks failure threshold passed (at least 50 failed to be sent)'
        })
        trackRecorderError(moteRecorderErrorMap.CHUNKS_ERROR_THRESHOLD_ERROR)
        break
      case moteRecorderErrorMap.MOTE_ID_UNDEFINED_ERROR:
        setError(moteRecorderErrorMap.MOTE_ID_UNDEFINED_ERROR)
        Diagnostics.dispatch({
          userVisibleErrorMessage:
            'We were unable to complete the recording. Please check your internet connection and try again or kindly contact support',
          dispatchContext: 'MOTE_RECORDER',
          title: 'Mote registration failed'
        })
        trackRecorderError(moteRecorderErrorMap.MOTE_ID_UNDEFINED_ERROR)
        clearPollingTimeout()
        break
      case moteRecorderErrorMap.FIX_MICROPHONE_PERMISSION:
      case 'NotFoundError':
      case 'NotAllowedError':
      case 'NotReadableError':
        setError(moteRecorderErrorMap.FIX_MICROPHONE_PERMISSION)
        clearPollingTimeout()
        trackRecorderError(moteRecorderErrorMap.FIX_MICROPHONE_PERMISSION)
        break
      case moteRecorderErrorMap.STREAM_UNAVAILABLE_ERROR:
        setError(moteRecorderErrorMap.UNKNOWN_ERROR)
        clearPollingTimeout()
        trackRecorderError(moteRecorderErrorMap.UNKNOWN_ERROR, {
          originatingErrorCode: moteRecorderErrorMap.STREAM_UNAVAILABLE_ERROR
        })
        break
      case 403:
        setError(moteRecorderErrorMap.NOT_LOGGED_IN)
        logD.warn('Not logged in')
        track({
          variables: { event: TRACK_EVENT_NAMES.error.moteCreateLogin }
        })
        break
      case 423:
        setError(moteRecorderErrorMap.QUOTA_EXCEEDED)
        logD.error('Quota limit exceeded', { httpStatus: 423 })
        track({ variables: { event: TRACK_EVENT_NAMES.quota.exhausted } })
        trackRecorderError(moteRecorderErrorMap.QUOTA_EXCEEDED)
        break
      case 428:
        setError(moteRecorderErrorMap.FORCE_UPGRADE)
        logD.error('Force upgrade extension requested', { httpStatus: 428 })
        track({
          variables: { event: TRACK_EVENT_NAMES.error.extensionOutdated }
        })
        trackRecorderError(moteRecorderErrorMap.FORCE_UPGRADE)
        break
      case 500:
        setError(moteRecorderErrorMap.UNKNOWN_ERROR)

        logD.error('Server error occurred', { httpStatus: 500 })

        Diagnostics.dispatch({
          userVisibleErrorMessage:
            'Whoops - something went wrong. Please try again, or search our FAQ pages for troubleshooting questions',
          dispatchContext: 'MOTE_RECORDER',
          title: 'Something went wrong in Recorder'
        })
        clearPollingTimeout()
        trackRecorderError(moteRecorderErrorMap.UNKNOWN_ERROR)
        break
      default:
        setError(moteRecorderErrorMap.UNKNOWN_ERROR)
        logD.error('Server error occurred', { httpStatus: '> 500' })

        Diagnostics.dispatch({
          userVisibleErrorMessage:
            'Whoops - something went wrong. Please try again, or search our FAQ pages for troubleshooting questions',
          dispatchContext: 'MOTE_RECORDER',
          title: 'Something went wrong in Recorder'
        })
        clearPollingTimeout()
        trackRecorderError(moteRecorderErrorMap.UNKNOWN_ERROR, {
          originatingErrorCode: errorCode
        })
    }

    cancelRecording()
  }, [uploaderError, recorderError])

  useEffect(() => {
    if (!shouldErrorOut) return
    onError(shouldErrorOut)
  }, [shouldErrorOut])

  function getFinalizedAdditionalPayload() {
    const additionalPayload = getRegisterExtraPayload()
    if ('isAnonymous' in additionalPayload) {
      return { isAnonymous: additionalPayload.isAnonymous }
    } else {
      return {}
    }
  }

  return {
    startRecording,
    stopRecording,
    cancelRecording,
    pauseRecording,
    resumeRecording,
    reset,
    flightState,
    error,
    status,
    audioContext,
    audioStream,
    streamingTranscriptData,
    seconds,
    maxSeconds,
    permissionStatus,
    permissionResponseReceived,
    chunksInFlight,
    loadingPollingLimit,
    registeredMote
  }
}
